import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { IntelligenceCard, IntelligenceCardStatus } from './IntelligenceCard';
import { useMessageResponseUrgency } from '@/hooks/useMessageResponseUrgency';

export const MessageResponseCard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useMessageResponseUrgency();

  const getStatus = (count: number): IntelligenceCardStatus => {
    if (count === 0) return 'success';
    if (count <= 2) return 'warning';
    return 'danger';
  };

  const handleReviewMessages = () => {
    navigate('/admin/communication', { 
      state: { 
        filterPending: true 
      } 
    });
  };

  const count = data?.count || 0;
  const status = getStatus(count);

  const getContextText = () => {
    if (count === 0) {
      return "All caught up on messages! 📬";
    }
    
    if (count === 1) {
      const message = data?.pending_messages[0];
      return `${message?.family_name} - ${message?.message_type} (${message?.days_pending} days ago)`;
    }
    
    const oldestMessage = data?.pending_messages.reduce((oldest, current) => 
      current.days_pending > oldest.days_pending ? current : oldest
    );
    
    return `Oldest message: ${oldestMessage?.days_pending} days from ${oldestMessage?.family_name}`;
  };

  const getStatusText = () => {
    if (count === 0) return 'All responded';
    if (count === 1) return '1 pending response';
    return `${count} pending responses`;
  };

  return (
    <IntelligenceCard
      title="Message Response Urgency"
      description="Family messages awaiting your response"
      icon={MessageSquare}
      metric={count}
      metricLabel={count === 1 ? "pending message" : "pending messages"}
      contextText={getContextText()}
      status={status}
      statusText={getStatusText()}
      actionLabel="Review Messages"
      onAction={handleReviewMessages}
      actionDisabled={count === 0}
      isLoading={isLoading}
      error={error?.message}
    />
  );
};